import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Orders } from 'src/app/models/Orders.model';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * Sub-modale "Lista prodotti per collo" — sub-modal nested aperto da
 * `confirm-boxes` quando l'utente clicca il bottone "Prodotti" su una
 * riga collo. Gestisce l'assegnazione dei barcode al box_order_id.
 *
 * Cleanup v2.11.0 J.4.a:
 * - 🚨 FIX DEAD: classi interne `OrderBox` + `OrderUpdate` duplicate del
 *   model Orders.OrderUpdate. Drop completo.
 * - 🚨 FIX DEAD: `initializeOrder()` mai chiamato.
 * - 🚨 FIX DEAD: ~80 LOC di blocchi `// commented` legacy (vecchie
 *   versioni di reloadFunction/selectProducts).
 * - 🚨 FIX DEAD: `presentToastOnBox` mai chiamato.
 * - 🚨 FIX BUG: `loading = !loading` toggle (potenziale stale state).
 *   Ora `loading = true/false` esplicito.
 * - Switch a inject() + takeUntilDestroyed.
 * - Drop ~25 console.log (incluso "MANANC CACAT" 🇷🇴, "mananci cacat lache" 🇷🇴
 *   ×2, "Well done mate", "Sub is", "Products With/Without Box", ecc.).
 */
@Component({
  selector: 'app-list-of-products-for-boxes',
  templateUrl: './list-of-products-for-boxes.component.html',
  styleUrls: ['./list-of-products-for-boxes.component.scss'],
  standalone: false,
})


export class ListOfProductsForBoxesComponent implements OnInit {

  @Input() order: any;
  @Input() openedProductsListBoxes = false;
  @Input() selectedBoxToAddProducts: any;
  @Output() modalClosed1 = new EventEmitter<void>();

  // === DI ===
  private destroyRef = inject(DestroyRef);
  private orderService = inject(OrdersService);
  public closeServ = inject(OpenModalService);

  // === State ===
  public orderToUpdate: Orders.OrderUpdate = new Orders.OrderUpdate(null);
  public loading = false;
  public numbers: number[] = [];
  public activeAccordion: string | null = null;

  // Split: prodotti senza box assegnato vs con box (per ridistribuzione).
  public productsWithoutBox: any[] = [];
  public productsWithBox: any[] = [];

  // Spin / animation state
  public isSpinning = false;
  public isSpinningReverse = false;

  // === Lifecycle ===

  ngOnInit(): void {
    this.reloadFunction();
  }

  close(): void {
    this.closeServ.offListOfProductsBoxes();
    this.loading = false;
    this.modalClosed1.emit();
  }

  // === Data load ===

  /**
   * Rebuild di productsWithoutBox + productsWithBox dai barcode dell'ordine.
   * Per ogni prodotto:
   * - barcodes con `box_id === null` → productsWithoutBox
   * - barcodes con `box_id !== null` → productsWithBox
   * Stessa logica per sub_products.
   */
  reloadFunction(): void {
    this.loading = true;
    this.orderToUpdate = new Orders.OrderUpdate(this.order);
    this.productsWithoutBox = [];
    this.productsWithBox = [];

    (this.orderToUpdate.products ?? []).forEach((prod: any) => {
      const hasValidBarcodes = prod.barcodes && prod.barcodes.length > 0;
      const hasSubProducts = prod.sub_products && prod.sub_products.length > 0;

      const prodWithoutBox: any = {
        ...prod,
        barcodes: [],
        sub_products: [],
        minQuantity: 0,
      };
      const prodWithBox: any = {
        ...prod,
        barcodes: [],
        sub_products: [],
        minQuantity: 0,
      };

      // Split barcodes del prodotto.
      if (hasValidBarcodes) {
        prod.barcodes.forEach((barcode: any) => {
          if (barcode.box_id === null) {
            prodWithoutBox.barcodes.push(barcode);
          } 
          else {
            prodWithBox.barcodes.push(barcode);
          }
        });
      }

      prodWithoutBox.minQuantity = prodWithoutBox.barcodes.length;
      prodWithBox.minQuantity = prodWithBox.barcodes.length;

      // Split sub-products + loro barcodes.
      if (hasSubProducts) {
        prod.sub_products.forEach((sub: any) => {
          const subWithoutBox: any = { ...sub, barcodes: [], minQuantity: 0 };
          const subWithBox: any = { ...sub, barcodes: [], minQuantity: 0 };

          sub.barcodes.forEach((barcode: any) => {
            if (barcode.box_id === null) {
              subWithoutBox.barcodes.push(barcode);
            } 
            else {
              subWithBox.barcodes.push(barcode);
            }
          });

          subWithoutBox.minQuantity = subWithoutBox.barcodes.length;
          subWithBox.minQuantity = subWithBox.barcodes.length;

          if (subWithoutBox.barcodes.length > 0) {
            prodWithoutBox.sub_products.push(subWithoutBox);
          }
          if (subWithBox.barcodes.length > 0) {
            prodWithBox.sub_products.push(subWithBox);
          }
        });

        prodWithoutBox.minQuantity += prodWithoutBox.op_qta_picked;
        prodWithBox.minQuantity += prodWithBox.op_qta_picked;
      }

      // Aggiungi solo se contiene barcodes o sub-products effettivi.
      if (
        prodWithoutBox.barcodes.length > 0 ||
        prodWithoutBox.sub_products.length > 0
      ) {
        this.productsWithoutBox.push(prodWithoutBox);
      }
      if (
        prodWithBox.barcodes.length > 0 ||
        prodWithBox.sub_products.length > 0
      ) {
        this.productsWithBox.push(prodWithBox);
      }
    });

    // Calcola minQuantity per sub-products in base alla proporzione.
    this.productsWithoutBox.forEach((prod) => {
      prod.sub_products.forEach((sub: any) => {
        sub.minQuantity = sub.osp_qty / +prod.op_qta_picked;
      });
    });
    this.productsWithBox.forEach((prod) => {
      prod.sub_products.forEach((sub: any) => {
        sub.minQuantity = sub.osp_qty / +prod.op_qta_picked;
      });
    });

    this.loading = false;
  }

  handleToggle(event: Event, value: string) {
    const details = event.target as HTMLDetailsElement;
    this.onAccordionChange({
      detail: {
        value: details.open ? value : null
      }
    });
  }

  onAccordionChange(event: any): void {
    this.activeAccordion = event?.detail?.value ?? null;
  }

  changeProductNumber(n: number): void {
    this.numbers = Array.from({ length: n }, (_, index) => index + 1);
  }

  // === Assegnazione barcode al box ===

  /**
   * Assegna `box_id = selectedBoxToAddProducts.box_order_id` ai barcode
   * del prodotto/sub-prodotto (fino a `minQuantity`).
   */
  selectProducts(prod: any): void {
    if (prod.sub_products && prod.sub_products.length > 0) {
      prod.sub_products.forEach((sub: any) => {
        let count = prod.minQuantity * sub.minQuantity;
        sub.barcodes.forEach((bar: any) => {
          while (count > 0 && bar.box_id === null) {
            bar.box_id = this.selectedBoxToAddProducts.box_order_id;
            count--;
          }
        });
      });
    } 
    else {
      let count = prod.minQuantity;
      prod.barcodes.forEach((bar: any) => {
        while (count > 0 && bar.box_id === null) {
          bar.box_id = this.selectedBoxToAddProducts.box_order_id;
          count--;
        }
      });
    }

    this.persistUpdate(prod);
  }

  /**
   * Rimuove `box_id` dai barcode del prodotto/sub-prodotto già assegnati
   * a `selectedBoxToAddProducts.box_order_id` (fino a `minQuantity`).
   */
  selectMatchingProducts(prod: any): void {
    const boxId = this.selectedBoxToAddProducts?.box_order_id;
    if (prod.sub_products && prod.sub_products.length > 0) {
      prod.sub_products.forEach((sub: any) => {
        let count = prod.minQuantity * sub.minQuantity;
        sub.barcodes.forEach((bar: any) => {
          while (count > 0 && bar.box_id === boxId) {
            bar.box_id = null;
            count--;
          }
        });
      });
    } 
    else {
      let count = prod.minQuantity;
      prod.barcodes.forEach((bar: any) => {
        while (count > 0 && bar.box_id === boxId) {
          bar.box_id = null;
          count--;
        }
      });
    }

    this.persistUpdate(prod);
  }

  /**
   * Persiste l'update sul backend + reload UI. Helper estratto dal
   * pattern copia-incollato in `selectProducts` e `selectMatchingProducts`.
   */
  private persistUpdate(prod: any): void {
    // Aggiorna il prodotto in orderToUpdate.products.
    const productIndex = this.orderToUpdate.products.findIndex(
      (p: any) => p.product_id === prod.product_id,
    );
    if (productIndex !== -1) {
      this.orderToUpdate.products[productIndex] = prod;
    }

    this.orderService
      .updateOrder(this.orderToUpdate)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data) => {
        if (data?.success) {
          this.toggleReverseSpin();
          this.reloadFunction();
        }
      });
  }

  // === Spin animations ===

  toggleSpin(): void {
    this.isSpinning = true;
    setTimeout(() => (this.isSpinning = false), 1000);
  }

  toggleReverseSpin(): void {
    this.isSpinningReverse = true;
    setTimeout(() => (this.isSpinningReverse = false), 1000);
  }

  // === Helper ===

  howManyBarcodes(item: any): number {
    return (
      item?.barcodes?.filter(
        (b: any) => b.barcode_text != null && b.barcode_text !== '',
      ).length ?? 0
    );
  }

}
